import {
  Activity,
  AlertCircle, Button, Calendar,
  CheckCircle,
  Clock,
  Download, Select, Target,
  TrendingDown,
  TrendingUp,
  Users
} from "@ews-admin/global-design-system";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useFacilityUser } from "../../../hooks/useFacilityUser";
import {
  fetchAnalyticsRequest,
  setAnalyticsTimeRange,
} from "../../../store/reducers/dashboardAnalyticsSlice";
import {
  selectAnalytics,
  selectAnalyticsError,
  selectAnalyticsLoading,
  selectTimeRange,
} from "../../../store/selectors/dashboardAnalyticsSelector";
import {
  exportAnalyticsToCSV,
  generateAnalyticsReport,
  generateMetricReport,
} from "../../../utils/report.utils";

type TopDoctor = {
  name: string;
  appointments: number;
  specialty: string;
};

interface MetricCardProps {
  title: string;
  value: string | number;
  change: {
    value: string;
    type: "increase" | "decrease" | "neutral";
  };
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  delay: number;
  animateCards: boolean;
  onDownload?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  icon: Icon,
  color,
  delay,
  animateCards,
  onDownload,
}) => {
  const getChangeIcon = () => {
    switch (change.type) {
      case "increase":
        return <TrendingUp className="w-4 h-4 text-success-600" />;
      case "decrease":
        return <TrendingDown className="w-4 h-4 text-error-600" />;
      default:
        return <Activity className="w-4 h-4 text-neutral-600" />;
    }
  };

  const getChangeColor = () => {
    switch (change.type) {
      case "increase":
        return "text-success-600";
      case "decrease":
        return "text-error-600";
      default:
        return "text-neutral-600";
    }
  };

  return (
    <div
      className={`group bg-white rounded-xl shadow-soft border border-neutral-200 p-6 hover:shadow-xl hover:border-primary-300 transition-all duration-500 hover-lift ${animateCards ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="flex justify-between items-center mb-4">
        <div
          className={`flex justify-center items-center w-12 h-12 rounded-lg transition-transform group-hover:scale-110 ${color}`}
        >
          <Icon className="w-6 h-6 text-white" />
        </div>
        {onDownload && (
          <Button
            variant="ghost"
            size="sm"
            className="opacity-0 group-hover:opacity-100 transition-opacity hover-scale"
            onClick={onDownload}
            title="Télécharger ce rapport"
          >
            <Download className="w-4 h-4" />
          </Button>
        )}
      </div>

      <div className="mb-2">
        <h3 className="mb-1 text-sm font-medium text-neutral-600">{title}</h3>
        <p className="text-3xl font-bold text-neutral-900">{value}</p>
      </div>

      <div className="flex items-center space-x-2">
        {getChangeIcon()}
        <span className={`text-sm font-medium ${getChangeColor()}`}>
          {change.value}
        </span>
      </div>
    </div>
  );
};

interface ChartCardProps {
  title: string;
  children: React.ReactNode;
  delay: number;
  animateCards: boolean;
  onDownload?: () => void;
}

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  children,
  delay,
  animateCards,
  onDownload,
}) => {
  return (
    <div
      className={`group bg-white rounded-xl shadow-soft border border-neutral-200 p-6 hover:shadow-xl hover:border-primary-300 transition-all duration-500 hover-lift ${animateCards ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-neutral-900">{title}</h3>
        {onDownload && (
          <Button
            variant="ghost"
            size="sm"
            className="opacity-0 group-hover:opacity-100 transition-opacity hover-scale"
            onClick={onDownload}
            title="Télécharger ce rapport"
          >
            <Download className="w-4 h-4" />
          </Button>
        )}
      </div>
      {children}
    </div>
  );
};

export const ReportsAnalytics: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const facilityUser = useFacilityUser();
  const analytics = useSelector(selectAnalytics);
  const analyticsLoading = useSelector(selectAnalyticsLoading);
  const analyticsError = useSelector(selectAnalyticsError);
  const currentTimeRange = useSelector(selectTimeRange); // Get from Redux store
  const [animateCards, setAnimateCards] = useState(false);
  const hasFetchedRef = useRef(false);

  // Fetch analytics ONLY when component mounts and no cache exists
  useEffect(() => {
    const facilityId = facilityUser?.healthcareFacility?.id;

    // If we already have analytics data, use it (CACHE)
    if (analytics) {
      return;
    }

    // If we're already loading or already initiated a fetch, don't fetch again
    if (analyticsLoading || hasFetchedRef.current) {
      return;
    }

    // Fetch only if we have facilityId and haven't fetched yet
    if (facilityId) {
      hasFetchedRef.current = true;
      dispatch(
        fetchAnalyticsRequest({ facilityId, timePeriod: currentTimeRange })
      );
    }
  }, [
    analytics,
    analyticsLoading,
    dispatch,
    facilityUser?.healthcareFacility?.id,
    currentTimeRange,
  ]);

  // Handle manual refresh
  const handleRefresh = () => {
    const facilityId = facilityUser?.healthcareFacility?.id;
    if (facilityId) {
      dispatch(
        fetchAnalyticsRequest({ facilityId, timePeriod: currentTimeRange })
      );
    }
  };

  // Handle time range change - update store and fetch new data
  const handleTimeRangeChange = (value: string) => {
    const facilityId = facilityUser?.healthcareFacility?.id;

    // Update time range in Redux store
    dispatch(setAnalyticsTimeRange(value));

    if (facilityId) {
      dispatch(fetchAnalyticsRequest({ facilityId, timePeriod: value }));
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimateCards(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleDownloadReport = (reportType: string) => {
    if (!analytics) {
      console.error("No analytics data available");
      return;
    }

    const facilityName =
      facilityUser?.healthcareFacility?.name || "Healthcare Facility";

    if (reportType === "pdf" || reportType === "all") {
      generateAnalyticsReport(analytics, currentTimeRange, facilityName);
    }

    if (reportType === "csv" || reportType === "all") {
      exportAnalyticsToCSV(analytics, currentTimeRange);
    }
  };

  const handleDownloadMetric = (
    metricType: "appointments" | "patients" | "doctors"
  ) => {
    if (!analytics) return;
    const facilityName =
      facilityUser?.healthcareFacility?.name || "Healthcare Facility";
    generateMetricReport(metricType, analytics, currentTimeRange, facilityName);
  };

  // Helper function to parse change string and determine type
  const parseChange = (
    changeString: string
  ): {
    value: string;
    type: "increase" | "decrease" | "neutral";
  } => {
    if (!changeString) {
      return { value: "0%", type: "neutral" };
    }
    const trimmed = changeString.trim();
    if (trimmed.startsWith("+")) {
      return { value: trimmed, type: "increase" };
    } else if (trimmed.startsWith("-")) {
      return { value: trimmed, type: "decrease" };
    }
    return { value: trimmed, type: "neutral" };
  };

  // Build metrics from analytics data
  const metrics = analytics
    ? [
      {
        title: t("reports.metrics.totalAppointments"),
        value: analytics.totalAppointments.toLocaleString(),
        change: parseChange(analytics.totalAppointmentsChange),
        icon: Calendar,
        color: "bg-primary-500",
        delay: 100,
        onDownload: () => handleDownloadMetric("appointments"),
      },
      {
        title: t("reports.metrics.activePatients"),
        value: analytics.activePatients.toLocaleString(),
        change: parseChange(analytics.activePatientsChange),
        icon: Users,
        color: "bg-secondary-700",
        delay: 200,
        onDownload: () => handleDownloadMetric("patients"),
      },
      {
        title: t("reports.metrics.completionRate"),
        value: `${analytics.completionRate}%`,
        change: parseChange(analytics.completionRateChange),
        icon: Target,
        color: "bg-warning-500",
        delay: 300,
        onDownload: () => handleDownloadMetric("appointments"),
      },
      {
        title: t("reports.metrics.totalDoctors"),
        value: analytics.totalDoctors.toLocaleString(),
        change: { value: "", type: "neutral" as const },
        icon: Users,
        color: "bg-primary-600",
        delay: 400,
        onDownload: () => handleDownloadMetric("doctors"),
      },
    ]
    : [
      {
        title: t("reports.metrics.totalAppointments"),
        value: "-",
        change: { value: "-", type: "neutral" as const },
        icon: Calendar,
        color: "bg-primary-500",
        delay: 100,
      },
      {
        title: t("reports.metrics.activePatients"),
        value: "-",
        change: { value: "-", type: "neutral" as const },
        icon: Users,
        color: "bg-secondary-700",
        delay: 200,
      },
      {
        title: t("reports.metrics.completionRate"),
        value: "-",
        change: { value: "-", type: "neutral" as const },
        icon: Target,
        color: "bg-warning-500",
        delay: 300,
      },
      {
        title: t("reports.metrics.totalDoctors"),
        value: "-",
        change: { value: "", type: "neutral" as const },
        icon: Users,
        color: "bg-primary-600",
        delay: 400,
      },
    ];

  // Build appointment stats from analytics data
  const appointmentStats = analytics
    ? [
      {
        label: t("reports.appointmentStats.completed"),
        value: analytics.appointmentStatistics.completed,
        color: "bg-success-500",
      },
      {
        label: t("reports.appointmentStats.cancelled"),
        value: analytics.appointmentStatistics.canceled,
        color: "bg-error-500",
      },
      {
        label: t("reports.appointmentStats.inProgress"),
        value: analytics.appointmentStatistics.inProgress,
        color: "bg-primary-500",
      },
      {
        label: t("reports.appointmentStats.confirmed"),
        value: analytics.appointmentStatistics.confirmed,
        color: "bg-warning-500",
      },
    ]
    : [];

  // Build top doctors from analytics data
  const topDoctors: TopDoctor[] = analytics
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
    analytics.topDoctors.map((doctor: any) => ({
      name: doctor.name,
      appointments: doctor.completedAppointments,
      specialty: doctor.specialty,
    }))
    : [];

  // Show error state
  if (analyticsError && !analytics) {
    return (
      <div className="p-6">
        <div className="p-4 text-center rounded-lg border bg-error-50 border-error-200">
          <p className="text-error-600">
            {t("reports.error")}: {analyticsError}
          </p>
          <button
            onClick={() => {
              const facilityId = facilityUser?.healthcareFacility?.id;
              if (facilityId) {
                dispatch(fetchAnalyticsRequest({ facilityId }));
              }
            }}
            className="px-4 py-2 mt-4 text-white rounded-lg bg-primary-500 hover:bg-primary-600"
          >
            {t("reports.retry")}
          </button>
        </div>
      </div>
    );
  }

  // Show empty state if no analytics data
  if (!analytics && !analyticsLoading) {
    return (
      <div className="p-6">
        <div className="p-8 text-center rounded-lg border bg-neutral-50 border-neutral-200">
          <p className="mb-4 text-neutral-600">{t("reports.noData")}</p>
          <button
            onClick={() => {
              const facilityId = facilityUser?.healthcareFacility?.id;
              if (facilityId) {
                dispatch(fetchAnalyticsRequest({ facilityId }));
              }
            }}
            className="px-4 py-2 text-white rounded-lg bg-primary-500 hover:bg-primary-600"
          >
            {t("reports.loadData")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fadeIn">
      {/* Header */}
      <div
        className={`transition-all duration-700 ${animateCards ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
      >
        <div className="flex justify-between items-start">
          <div>
            <h1 className="mb-2 text-3xl font-bold text-neutral-900">
              {t("reports.title")}
            </h1>
            <p className="text-neutral-600">{t("reports.subtitle")}</p>
          </div>
          <Button
            onClick={handleRefresh}
            disabled={analyticsLoading}
            className="flex items-center space-x-2"
            variant="outline"
          >
            <svg
              className={`w-4 h-4 ${analyticsLoading ? "animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span>{t("reports.refresh")}</span>
          </Button>
        </div>
      </div>

      {/* Controls */}
      <div
        className={`relative z-50 bg-white rounded-xl shadow-soft border border-neutral-200 p-4 transition-all duration-700 ${animateCards ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        style={{ transitionDelay: "200ms" }}
      >
        <div className="flex flex-col justify-between items-start space-y-4 sm:flex-row sm:items-center sm:space-y-0">
          <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-4">
            <div className="relative z-50">
              <label className="block mb-2 text-sm font-medium text-neutral-700">
                {t("reports.timePeriod")}
              </label>
              <Select
                value={currentTimeRange}
                onChange={(value) => handleTimeRangeChange(value as string)}
                options={[
                  { label: t("reports.timeRange.week"), value: "week" },
                  { label: t("reports.timeRange.month"), value: "month" },
                  { label: t("reports.timeRange.quarter"), value: "quarter" },
                  { label: t("reports.timeRange.year"), value: "year" },
                  { label: t("reports.timeRange.all"), value: "all" },
                ]}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => handleDownloadReport("pdf")}
              disabled={!analytics || analyticsLoading}
              className="hover-scale shadow-sm"
              variant="outline"
            >
              <Download className="mr-2 w-4 h-4" />
              <span className="hidden sm:inline">PDF</span>
            </Button>
            <Button
              onClick={() => handleDownloadReport("csv")}
              disabled={!analytics || analyticsLoading}
              className="hover-scale shadow-sm"
              variant="outline"
            >
              <Download className="mr-2 w-4 h-4" />
              <span className="hidden sm:inline">CSV</span>
            </Button>
            <Button
              onClick={() => handleDownloadReport("all")}
              disabled={!analytics || analyticsLoading}
              className="hover-scale shadow-md bg-ews-primary hover:bg-ews-primary-hover"
            >
              <Download className="mr-2 w-4 h-4" />
              <span className="font-semibold">{t("reports.downloadAll")}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid relative z-10 grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {metrics.map((metric, index) => (
          <MetricCard
            key={index}
            {...metric}
            delay={metric.delay}
            animateCards={animateCards}
          />
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Appointment Statistics */}
        <ChartCard
          title={t("reports.appointmentStatistics")}
          delay={500}
          animateCards={animateCards}
          onDownload={() => handleDownloadMetric("appointments")}
        >
          <div className="space-y-4">
            {appointmentStats.map((stat, index) => (
              <div key={index} className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${stat.color}`}></div>
                  <span className="text-sm font-medium text-neutral-700">
                    {stat.label}
                  </span>
                </div>
                <span className="text-sm font-semibold text-neutral-900">
                  {stat.value}
                </span>
              </div>
            ))}
            {analytics && (
              <div className="pt-4 border-t border-neutral-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-neutral-700">
                    {t("reports.total")}
                  </span>
                  <span className="text-lg font-bold text-neutral-900">
                    {analytics.appointmentStatistics.total.toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </div>
        </ChartCard>

        {/* Top Performing Doctors */}
        <ChartCard
          title={t("reports.topPerformingDoctors")}
          delay={800}
          animateCards={animateCards}
          onDownload={() => handleDownloadMetric("doctors")}
        >
          <div className="space-y-4">
            {topDoctors.map((doctor: TopDoctor) => (
              <div
                key={doctor.name}
                className="flex items-center p-3 space-x-3 rounded-lg transition-colors duration-200 hover:bg-neutral-50"
              >
                <div className="flex justify-center items-center w-8 h-8 rounded-full bg-ews-primary-100">
                  <Users className="w-4 h-4 text-primary-600" />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-neutral-900">
                      {doctor.name}
                    </span>
                    <span className="text-sm font-semibold text-primary-600">
                      {doctor.appointments}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-neutral-500">
                      {doctor.specialty}
                    </span>
                    <span className="text-xs text-neutral-400">
                      {t("reports.appointments")}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Additional Insights */}
      <div
        className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-700 ${animateCards ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        style={{ transitionDelay: "900ms" }}
      >
        {/* Quick Stats */}
        <div className="p-6 text-white rounded-xl bg-ews-primary">
          <div className="flex justify-between items-center mb-4">
            <Clock className="w-8 h-8" />
            <span className="text-2xl font-bold">
              {analytics ? `${analytics.completionRate}%` : "-"}
            </span>
          </div>
          <h3 className="mb-2 text-lg font-semibold">
            {t("reports.insights.completionRate")}
          </h3>
          <p className="text-sm text-ews-primary-100">
            {t("reports.insights.completionDescription")}
          </p>
        </div>

        <div className="p-6 text-white rounded-xl bg-ews-secondary">
          <div className="flex justify-between items-center mb-4">
            <CheckCircle className="w-8 h-8" />
            <span className="text-2xl font-bold">
              {analytics ? `${analytics.patientSatisfaction}%` : "-"}
            </span>
          </div>
          <h3 className="mb-2 text-lg font-semibold">
            {t("reports.insights.patientSatisfaction")}
          </h3>
          <p className="text-sm text-success-100">
            {t("reports.insights.satisfactionDescription")}
          </p>
        </div>

        <div className="p-6 text-white rounded-xl bg-ews-primary-hover">
          <div className="flex justify-between items-center mb-4">
            <AlertCircle className="w-8 h-8" />
            <span className="text-2xl font-bold">
              {analytics ? analytics.pendingReviews : "-"}
            </span>
          </div>
          <h3 className="mb-2 text-lg font-semibold">
            {t("reports.insights.pendingReviews")}
          </h3>
          <p className="text-sm text-warning-100">
            {t("reports.insights.reviewsDescription")}
          </p>
        </div>
      </div>
    </div>
  );
};
